import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel,
  IconButton, InputAdornment, Link, Skeleton, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  appModalActionsSx, appModalCloseButtonSx, appModalDangerButtonSx, appModalDangerLoadingSx, appModalFieldSx, appModalHeaderDividerSx,
  appModalPaperSx, appModalPrimaryButtonSx, appModalPrimaryLoadingSx, appModalSecondaryButtonSx, appModalTitleBarSx, appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import { colors } from '../../styles/theme';
import { updateMetaObject } from '../../services/metaAdsService';
import { formatMetaCountdown } from '../metaAdsConfig';
import { formatMetaMoney } from '../metaAdsMoney';
import {
  META_BUDGET_ACTIONS, META_RENAME_MAX, META_WRITE_EDIT_ACTIONS, META_WRITE_UNCONFIRMED_MESSAGE,
  metaBudgetInputValue, metaWriteActionLabel, metaWritePreviewNotice, metaWriteStatusLabel, newMetaIdempotencyKey,
  isMetaWriteRetryableError, parseMetaBudgetInput, readMetaWriteDryRun, readMetaWriteError, readMetaWriteResult, validateMetaBudget, validateMetaEndDate,
  validateMetaRename,
} from '../metaWrite';
import { metaAdsSkeletonSx } from './metaAdsUi.styles';
import { stripMetaTerrioMark } from '../metaFormat';
import { useMetaCountdown } from '../hooks/useMetaCountdown';
import MetaWriteNote, { metaNoteButtonSx } from './MetaWriteNote';

/*
 * F2a + F2b · Diálogo de una acción en Meta: `dry_run` → vista previa → confirmación con `expected` +
 * `idempotency_key`. Renombrar, presupuesto y fecha de fin piden primero el valor. Lo que puede gastar
 * plata real (`requires_double_confirm`) pide tildar «Entendido» (decisión 5). Eliminar tiene un
 * segundo paso donde hay que escribir ELIMINAR (sin Enter). Nunca reintenta solo: ni ante 429
 * (cuenta regresiva con el botón en gris) ni ante «No pudimos confirmar» (solo «Ver en Meta»).
 */

const DELETE_WORD = 'ELIMINAR';

// Aviso y cuenta regresiva compartidos con «Crear campaña» (MetaWriteNote / useMetaCountdown).
const Note = MetaWriteNote;
const noteButtonSx = metaNoteButtonSx;
const rowLabelSx = { fontSize: '12.5px', color: colors.textSecondary };
const rowValueSx = { fontSize: '13px', color: '#FBFBFB', overflowWrap: 'anywhere' };

function StatusChip({ status, level }) {
  const active = String(status || '').toUpperCase() === 'ACTIVE';
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 22, px: 1, borderRadius: '11px', fontSize: '12px', fontWeight: 600,
        bgcolor: active ? 'rgba(76,175,80,0.14)' : 'rgba(255,255,255,0.08)', color: active ? '#8BD18E' : '#D4D4D4',
      }}
    >
      {active ? <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50' }} aria-hidden /> : null}
      {metaWriteStatusLabel(status, level)}
    </Box>
  );
}

/** Antes → Después (estado, nombre, monto o fecha). */
function BeforeAfter({ preview, level, action, originalName, currency = null }) {
  if (!preview) return null;
  const rows = [];
  const isBudget = META_BUDGET_ACTIONS.includes(action);
  if (isBudget || action === 'set_end_time') {
    let label = 'Fecha de fin';
    if (isBudget) label = action === 'set_lifetime_budget' ? 'Total' : 'Diario';
    rows.push(
      <Box key="value" sx={{ display: 'contents' }}>
        <Typography sx={rowLabelSx}>{label}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }} data-testid="meta-write-value-change">
          <Typography component="span" sx={{ ...rowValueSx, color: colors.textSecondary }} data-testid="meta-write-before-value">
            {preview.beforeText || (action === 'set_end_time' ? 'Sin fecha' : '—')}
          </Typography>
          <Box component="span" sx={{ color: colors.textSecondary }} aria-label="pasa a">→</Box>
          <Typography component="span" sx={{ ...rowValueSx, fontWeight: 600 }} data-testid="meta-write-after-value">{preview.afterText || '—'}</Typography>
        </Box>
      </Box>,
    );
    if (isBudget && preview.maxAllowed != null && preview.increase) {
      rows.push(
        <Box key="max" sx={{ display: 'contents' }}>
          <Typography sx={rowLabelSx}>Máximo</Typography>
          <Typography sx={{ ...rowValueSx, color: colors.textSecondary }} data-testid="meta-write-max-allowed">
            {formatMetaMoney(preview.maxAllowed, preview.currency || currency)}
          </Typography>
        </Box>,
      );
    }
    return (
      <Box
        sx={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', columnGap: 1.5, rowGap: 1, alignItems: 'center', px: 1.5, py: 1.25, borderRadius: '8px', bgcolor: colors.backgroundSurface }}
        data-testid="meta-write-before-after"
      >
        {rows}
      </Box>
    );
  }
  const nameChanged = preview.before.name && originalName && preview.before.name !== originalName;
  if (action === 'rename' || nameChanged) {
    rows.push(
      <Box key="name" sx={{ display: 'contents' }}>
        <Typography sx={rowLabelSx}>Nombre</Typography>
        <Box sx={{ minWidth: 0 }}>
          {action === 'rename' ? (
            <>
              <Typography sx={{ ...rowValueSx, color: colors.textSecondary }} data-testid="meta-write-before-name">{preview.before.name}</Typography>
              <Typography sx={{ ...rowValueSx, fontWeight: 600 }} data-testid="meta-write-after-name">{preview.after.name}</Typography>
            </>
          ) : (
            <Typography sx={{ ...rowValueSx, fontWeight: 600 }} data-testid="meta-write-current-name">{preview.before.name}</Typography>
          )}
        </Box>
      </Box>,
    );
  }
  if (action !== 'rename' && rows.length === 0) {
    // Solo cambia el estado: sin label, los chips centrados a todo el ancho.
    return (
      <Box
        role="group"
        aria-label="Estado"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, px: 1.5, py: 1.5, borderRadius: '8px', bgcolor: colors.backgroundSurface }}
        data-testid="meta-write-before-after"
        data-layout="status-only"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }} data-testid="meta-write-status-change">
          <StatusChip status={preview.before.status} level={level} />
          <Box component="span" sx={{ color: colors.textSecondary, fontSize: '15px', lineHeight: 1 }} aria-label="pasa a">→</Box>
          <StatusChip status={preview.after.status} level={level} />
        </Box>
      </Box>
    );
  }
  if (action !== 'rename') {
    rows.push(
      <Box key="status" sx={{ display: 'contents' }}>
        <Typography sx={rowLabelSx}>Estado</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="meta-write-status-change">
          <StatusChip status={preview.before.status} level={level} />
          <Box component="span" sx={{ color: colors.textSecondary }} aria-label="pasa a">→</Box>
          <StatusChip status={preview.after.status} level={level} />
        </Box>
      </Box>,
    );
  }
  return (
    <Box
      sx={{ display: 'grid', gridTemplateColumns: '64px minmax(0, 1fr)', columnGap: 1.5, rowGap: 1, alignItems: 'center', px: 1.5, py: 1.25, borderRadius: '8px', bgcolor: colors.backgroundSurface }}
      data-testid="meta-write-before-after"
    >
      {rows}
    </Box>
  );
}

const PRIMARY = {
  pause: 'Pausar', resume: 'Reactivar', rename: 'Renombrar', set_daily_budget: 'Cambiar', set_lifetime_budget: 'Cambiar',
  set_end_time: 'Cambiar', archive: 'Archivar', delete: 'Eliminar',
};

/** Valor inicial del paso de edición. */
function initialValue(target) {
  if (!target) return '';
  if (target.action === 'rename') return stripMetaTerrioMark(target.name || '');
  if (META_BUDGET_ACTIONS.includes(target.action)) return metaBudgetInputValue(target.currentAmount);
  if (target.action === 'set_end_time') return /^\d{4}-\d{2}-\d{2}/.test(target.endsAt || '') ? target.endsAt.slice(0, 10) : '';
  return '';
}

/** Hoy (`YYYY-MM-DD`, hora local) para el mínimo del selector de fecha. */
function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Errores que se muestran debajo del campo (y vuelven al paso de edición).
const FIELD_BUDGET_REASONS = ['above_increase_pct', 'above_ceiling', 'below_spent', 'meta_rejected', 'currency_without_ceiling'];
function isFieldError(err, action) {
  if (!META_WRITE_EDIT_ACTIONS.includes(action)) return false;
  if (err.kind === 'validation' && (err.field === 'value' || err.field === 'currency')) return true;
  return err.kind === 'budget_invalid' && (err.field === 'value' || FIELD_BUDGET_REASONS.includes(err.reason));
}

/**
 * @param {{ target: { level: 'campaign'|'adset'|'ad', action: string, id: string, name: string,
 *   currency?: string|null, currentAmount?: number|null, endsAt?: string|null }|null,
 *   onClose: () => void, onApplied: (result: object, target: object) => void, onOpenSettings?: () => void,
 *   onReconnect?: () => void, onStale?: () => void, notFound?: { message: string, metaUrl?: string|null }|null }} props
 *   `onStale`: recargar la lista en silencio (STALE, «No pudimos confirmar», objeto que ya no está).
 *   `notFound` (solo «Eliminar lo creado» de crear campaña): el objeto existe en Meta pero Terrio todavía no lo
 *   sincronizó → `META_*_NOT_FOUND` es un aviso ámbar con ese texto, link a Meta y «Reintentar» (preview de nuevo)
 *   en vez del error final. Sin la prop, F2a no cambia.
 */
export default function MetaWriteDialog({
  target, onClose, onApplied, onOpenSettings = null, onReconnect = null, onStale = null, notFound = null,
}) {
  const open = Boolean(target);
  const level = target?.level || 'campaign';
  const action = target?.action || 'pause';
  const isEdit = META_WRITE_EDIT_ACTIONS.includes(action);
  const isBudget = META_BUDGET_ACTIONS.includes(action);
  const isDelete = action === 'delete';
  const [stage, setStage] = useState('loading');
  // Valor del paso de edición: nombre, monto o fecha (`name` por compatibilidad con renombrar).
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [typed, setTyped] = useState('');
  const [dry, setDry] = useState(null);
  const [error, setError] = useState(null);
  const [checked, setChecked] = useState(false);
  const keyRef = useRef(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const countdown = useMetaCountdown();
  const { start: startCountdown, reset: resetCountdown } = countdown;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; abortRef.current?.abort(); };
  }, []);

  const handleError = useCallback((err) => {
    setError(err);
    if (err.kind === 'rate_limited') startCountdown(err.retryAfterS);
  }, [startCountdown]);

  const valueToSend = useCallback((raw) => {
    if (!target) return undefined;
    if (META_BUDGET_ACTIONS.includes(target.action)) return parseMetaBudgetInput(raw) ?? '';
    if (target.action === 'rename' || target.action === 'set_end_time') return String(raw || '').trim();
    return undefined;
  }, [target]);

  const runPreview = useCallback(async (value, { keepError = null } = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStage('loading');
    setError(keepError);
    resetCountdown();
    let res;
    try {
      res = await updateMetaObject({
        level: target.level, id: target.id, action: target.action, value, currency: target.currency || null, dryRun: true,
      }, { signal: controller.signal });
    } catch (e) {
      if (e?.name === 'AbortError') return;
      throw e;
    }
    if (!mountedRef.current || controller.signal.aborted) return;
    if (!res?._httpOk) {
      const editing = META_WRITE_EDIT_ACTIONS.includes(target.action);
      setStage(editing ? 'edit' : 'preview');
      setDry(null);
      const err = readMetaWriteError(res);
      if (isFieldError(err, target.action)) {
        setNameError(err.message);
        return;
      }
      handleError(err);
      return;
    }
    setDry(readMetaWriteDryRun(res));
    keyRef.current = newMetaIdempotencyKey();
    setChecked(false);
    setStage('preview');
  }, [target, handleError, resetCountdown]);

  useEffect(() => {
    if (!target) return;
    setDry(null);
    setError(null);
    setChecked(false);
    setNameError('');
    setTyped('');
    resetCountdown();
    keyRef.current = null;
    if (META_WRITE_EDIT_ACTIONS.includes(target.action)) {
      setName(initialValue(target));
      setStage('edit');
    } else {
      runPreview();
    }
    // Solo al abrir otra acción.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const submitEdit = () => {
    let msg = '';
    if (action === 'rename') msg = validateMetaRename(name);
    else if (isBudget) msg = validateMetaBudget(name);
    else if (action === 'set_end_time') msg = validateMetaEndDate(name);
    setNameError(msg);
    if (msg) return;
    runPreview(valueToSend(name));
  };

  const confirm = async () => {
    if (!dry?.expected) return;
    setStage('confirming');
    setError(null);
    if (!keyRef.current) keyRef.current = newMetaIdempotencyKey();
    const res = await updateMetaObject({
      level,
      id: target.id,
      action,
      value: valueToSend(name),
      currency: isBudget ? target.currency || null : null,
      confirmText: isDelete ? typed.trim() : null,
      expected: dry.expected,
      idempotencyKey: keyRef.current,
    });
    if (!mountedRef.current) return;
    if (res?._httpOk) {
      onApplied(readMetaWriteResult(res), target);
      return;
    }
    const err = readMetaWriteError(res);
    // Eliminar vuelve al paso de ELIMINAR (salvo STALE: hay que revisar el preview nuevo).
    setStage(isDelete && err.kind !== 'stale' ? 'typed' : 'preview');
    if (err.kind === 'stale') {
      // Valores nuevos de Meta: preview rearmado, key nuevo, casilla destildada.
      setDry((prev) => ({ ...prev, preview: err.preview || prev?.preview, expected: err.expected || prev?.expected }));
      keyRef.current = newMetaIdempotencyKey();
      setChecked(false);
      setError(err);
      onStale?.();
      return;
    }
    if (err.kind === 'key_reused') {
      keyRef.current = null;
      if (err.previousStatus === 'unconfirmed') {
        setError({ ...err, kind: 'unconfirmed', message: META_WRITE_UNCONFIRMED_MESSAGE });
        return;
      }
      runPreview(valueToSend(name), { keepError: { ...err, kind: 'info', message: 'Revisá y confirmá de nuevo.' } });
      return;
    }
    // Red caída o un cambio todavía en curso: se reintenta con el mismo key (si ya se aplicó, vuelve `replayed`).
    if (err.kind !== 'network' && err.kind !== 'in_progress') keyRef.current = newMetaIdempotencyKey();
    if (err.kind === 'not_found' && notFound) {
      // Todavía no sincronizado: se vuelve a empezar por el preview (que pide ELIMINAR de nuevo).
      setDry(null);
      setStage('preview');
    }
    handleError(err);
    // Sin saber qué quedó en Meta (o el objeto ya no está): la lista se recarga en silencio.
    if (err.kind === 'unconfirmed' || err.kind === 'not_found') onStale?.();
  };

  if (!open) return null;

  const preview = dry?.preview || null;
  // Eliminar: la segunda confirmación es escribir ELIMINAR (no la casilla).
  const needsCheck = Boolean(preview?.requiresDoubleConfirm) && !isDelete;
  const waiting = countdown.left > 0;
  const syncPending = Boolean(notFound) && error?.kind === 'not_found';
  const blocking = error && !syncPending && [
    'unconfirmed', 'owner_only', 'disabled', 'blocked', 'not_allowed', 'not_found', 'permission', 'not_enabled', 'budget_level',
  ].includes(error.kind);
  const busy = stage === 'loading' || stage === 'confirming';
  const title = metaWriteActionLabel(level, action);
  // UN solo aviso, armado con los `code` del preview (rojo al eliminar, ámbar con plata real / tope).
  const notice = preview ? metaWritePreviewNotice(preview, level, action) : null;
  const realMoney = Boolean(preview?.warnings.some((w) => w.code === 'REAL_MONEY'));

  const errorNote = (() => {
    if (!error) return null;
    if (error.kind === 'rate_limited') {
      return (
        <Note tone="warn" testId="meta-write-rate-limited">
          <strong>{error.message}</strong>
          {waiting ? ` · Reintentá en ${formatMetaCountdown(countdown.left)}` : ' · Ya podés reintentar'}
        </Note>
      );
    }
    if (error.kind === 'stale' || error.kind === 'info') {
      return <Note tone="warn" testId={`meta-write-${error.kind}`}>{error.message}</Note>;
    }
    if (error.kind === 'in_progress') return <Note tone="info" testId="meta-write-in-progress">{error.message}</Note>;
    if (syncPending) {
      const link = notFound.metaUrl ? (
        <Link href={notFound.metaUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ ...noteButtonSx, display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-testid="meta-write-open-meta">
          Ver en Meta <OpenInNewIcon sx={{ fontSize: 13 }} aria-hidden />
        </Link>
      ) : null;
      return <Note tone="warn" action={link} testId="meta-write-not-synced">{notFound.message}</Note>;
    }
    let act = null;
    if (error.kind === 'unconfirmed' && error.metaUrl) {
      act = (
        <Link href={error.metaUrl} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ ...noteButtonSx, display: 'inline-flex', alignItems: 'center', gap: 0.5 }} data-testid="meta-write-open-meta">
          Ver en Meta <OpenInNewIcon sx={{ fontSize: 13 }} aria-hidden />
        </Link>
      );
    } else if (error.kind === 'permission' && onReconnect) {
      act = <Button size="small" onClick={onReconnect} sx={noteButtonSx} data-testid="meta-write-reconnect">Reconectar</Button>;
    } else if (error.kind === 'not_enabled' && onOpenSettings) {
      act = <Button size="small" onClick={onOpenSettings} sx={noteButtonSx} data-testid="meta-write-open-settings">Ir a Configuración de Meta</Button>;
    }
    return <Note tone="error" action={act} testId={`meta-write-error-${error.kind}`}>{error.message}</Note>;
  })();

  const fieldSx = {
    ...appModalFieldSx,
    '& .MuiInputLabel-root': { color: colors.textSecondary },
    '& .MuiInputLabel-root.Mui-focused': { color: colors.primaryLight },
    '& .MuiFormHelperText-root': { mx: 0, color: nameError ? colors.error : colors.textSecondary },
  };
  let body;
  if (stage === 'edit' && isBudget) {
    const current = target.currentAmount != null ? formatMetaMoney(target.currentAmount, target.currency) : null;
    body = (
      <TextField
        value={name}
        onChange={(e) => { setName(e.target.value); setNameError(''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitEdit(); } }}
        label={action === 'set_lifetime_budget' ? 'Presupuesto total' : 'Presupuesto diario'}
        fullWidth
        autoFocus
        size="small"
        error={Boolean(nameError)}
        helperText={nameError || (current ? `Hoy: ${current}` : ' ')}
        InputProps={{
          startAdornment: target.currency ? <InputAdornment position="start"><Box component="span" sx={{ color: colors.textSecondary, fontSize: '13px' }}>{target.currency}</Box></InputAdornment> : null,
        }}
        inputProps={{ inputMode: 'decimal', maxLength: 20, 'data-testid': 'meta-write-amount-input' }}
        sx={fieldSx}
      />
    );
  } else if (stage === 'edit' && action === 'set_end_time') {
    body = (
      <TextField
        type="date"
        value={name}
        onChange={(e) => { setName(e.target.value); setNameError(''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitEdit(); } }}
        label="Termina el"
        fullWidth
        autoFocus
        size="small"
        error={Boolean(nameError)}
        helperText={nameError || 'A las 23:59, hora de la cuenta.'}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: todayIso(), 'data-testid': 'meta-write-date-input' }}
        sx={{ ...fieldSx, '& input::-webkit-calendar-picker-indicator': { filter: 'invert(0.8)' } }}
      />
    );
  } else if (stage === 'typed') {
    body = (
      <>
        <Note tone="error" testId="meta-write-delete-final">Se elimina de Meta y no se puede deshacer.</Note>
        <TextField
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          // Sin atajos: Enter no confirma (§4, `irreversible`).
          onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
          label={`Escribí ${DELETE_WORD} para confirmar`}
          fullWidth
          autoFocus
          size="small"
          autoComplete="off"
          inputProps={{ 'data-testid': 'meta-write-typed-input', spellCheck: false, 'aria-label': `Escribí ${DELETE_WORD} para confirmar` }}
          sx={{ ...fieldSx, '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: colors.error } }}
        />
      </>
    );
  } else if (stage === 'edit') {
    body = (
      <TextField
        value={name}
        onChange={(e) => { setName(e.target.value.replace(/[\r\n]+/g, ' ')); setNameError(''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submitEdit(); } }}
        label="Nombre"
        fullWidth
        autoFocus
        size="small"
        error={Boolean(nameError)}
        helperText={nameError || `${name.trim().length}/${META_RENAME_MAX}`}
        inputProps={{ maxLength: META_RENAME_MAX + 20, 'data-testid': 'meta-write-name-input' }}
        sx={fieldSx}
      />
    );
  } else if (stage === 'loading') {
    body = (
      <Box data-testid="meta-write-loading" aria-busy="true">
        <Skeleton variant="rounded" height={14} sx={{ ...metaAdsSkeletonSx, width: '88%', mb: 1 }} />
        <Skeleton variant="rounded" height={14} sx={{ ...metaAdsSkeletonSx, width: '60%', mb: 1.5 }} />
        <Skeleton variant="rounded" height={44} sx={metaAdsSkeletonSx} />
      </Box>
    );
  } else if (preview || dry?.noChange) {
    body = (
      <>
        <BeforeAfter preview={preview} level={level} action={action} originalName={target.name} currency={target.currency} />
        {notice ? (
          <Note tone={notice.tone} testId="meta-write-notice" codes={notice.codes}>{notice.text}</Note>
        ) : null}
        {dry?.noChange ? <Note tone="info" testId="meta-write-no-change">Ya está así en Meta.</Note> : null}
        {needsCheck && !dry?.noChange ? (
          <FormControlLabel
            control={(
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                disabled={busy}
                size="small"
                sx={{ color: colors.textSecondary, '&.Mui-checked': { color: colors.primary } }}
                inputProps={{ 'data-testid': 'meta-write-double-confirm' }}
              />
            )}
            label={realMoney || action !== 'resume' ? 'Entendido' : 'Confirmo que quiero reactivar'}
            sx={{ m: 0, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: '13px', color: '#FBFBFB', pt: '9px' } }}
          />
        ) : null}
      </>
    );
  } else {
    body = null;
  }

  const typedOk = typed.trim() === DELETE_WORD;
  // «Reintentar» del preview que falló: habilitado si el error es recuperable (red, 5xx, límite ya
  // vencido, en curso) o si el objeto todavía no está sincronizado (crear a medias).
  const previewRetry = stage === 'preview' && !preview && (syncPending || isMetaWriteRetryableError(error));
  const primaryDisabled = busy || waiting || blocking
    || (stage === 'preview' && !previewRetry && (!dry?.expected || dry?.noChange || (needsCheck && !checked)))
    || (stage === 'typed' && (!typedOk || !dry?.expected));
  const showPrimary = !(error?.kind === 'unconfirmed') && !(stage === 'preview' && dry?.noChange);
  let primaryLabel = PRIMARY[action];
  if (stage === 'edit') primaryLabel = 'Continuar';
  else if (stage === 'preview' && !preview) primaryLabel = 'Reintentar';
  else if (stage === 'preview' && isDelete) primaryLabel = 'Continuar';
  const onPrimary = () => {
    if (stage === 'edit') submitEdit();
    else if (stage === 'preview' && !preview) runPreview(valueToSend(name));
    else if (stage === 'preview' && isDelete) { setStage('typed'); setError(null); setTyped(''); }
    else confirm();
  };
  // Colores (docs/ui/botones-y-estados.md): primario de Terrio siempre; solo la confirmación final de
  // eliminar (escribir ELIMINAR → «Eliminar») va en el rojo de borrado de Terrio (`ConfirmDialog` danger).
  const deleting = isDelete && (stage === 'typed' || stage === 'confirming');
  const loading = stage === 'confirming' || stage === 'loading';
  let primarySx = deleting ? appModalDangerButtonSx : appModalPrimaryButtonSx;
  if (loading) primarySx = { ...primarySx, ...(deleting ? appModalDangerLoadingSx : appModalPrimaryLoadingSx) };
  let secondary;
  if (stage === 'preview' && isEdit && preview) {
    secondary = <Button variant="outlined" onClick={() => { setStage('edit'); setError(null); }} disabled={busy} sx={appModalSecondaryButtonSx}>Editar</Button>;
  } else if (stage === 'typed') {
    secondary = <Button variant="outlined" onClick={() => { setStage('preview'); setError(null); }} disabled={busy} sx={appModalSecondaryButtonSx}>Volver</Button>;
  } else {
    secondary = <Button variant="outlined" onClick={onClose} disabled={busy} sx={appModalSecondaryButtonSx}>{showPrimary ? 'Cancelar' : 'Cerrar'}</Button>;
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth={false}
      PaperProps={{ sx: { ...appModalPaperSx, width: 'min(460px, 95vw)', maxWidth: '460px', height: 'auto' }, 'data-testid': 'meta-write-dialog' }}
      aria-labelledby="meta-write-title"
    >
      <DialogTitle id="meta-write-title" component="div" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>{title}</Typography>
        <IconButton size="small" onClick={onClose} disabled={busy} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ px: '20px', pt: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {action !== 'rename' ? (
          <Typography sx={{ fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere', color: '#FBFBFB' }}>{target.name}</Typography>
        ) : null}
        {body}
        {errorNote}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        {secondary}
        {showPrimary ? (
          <Button
            variant="contained"
            onClick={onPrimary}
            disabled={primaryDisabled}
            sx={primarySx}
            startIcon={stage === 'confirming' ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
            data-testid="meta-write-primary"
            data-tone={deleting ? 'danger' : 'primary'}
          >
            {primaryLabel}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
